import {
  MouseEvent,
  ReactElement,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import DotsVertical from "chameleon/icons/dots-vertical";
import {
  dropdownCoreClassLi,
  dropdownCoreClassUl,
} from "chameleon/lib/components/dropdown/drop-down-options/drop-down.css";
import {
  flyoutDropdownCoreClassLi,
  flyoutDropdownCoreClassUl,
} from "chameleon/lib/components/flyout/flyout.css";

import { Button } from "../button/button";
import {
  DropdownBehaviour,
  DropdownBehaviourHorizontalPositionEnum,
} from "../dropdown-behaviour/dropdown-behaviour";

export interface FlyoutProps {
  /**
   * OptionsArray
   */
  optionsArray: Array<ReactElement | null>;
  /**
   * Aria Label for accessibility
   */
  ariaLabel?: string;
  /**
   * Optional button svg
   */
  buttonSvg?: ReactElement;
  /**
   * Optional button text
   */
  buttonText?: string;
  /**
   * Optional match width with button
   */
  matchWidthWithButton?: boolean;
}

export const Flyout = ({
  optionsArray,
  ariaLabel,
  buttonSvg,
  buttonText = "",
  matchWidthWithButton = false,
}: FlyoutProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLUListElement>(null);

  const onClickMenu = (e?: MouseEvent) => {
    e?.stopPropagation();
    setIsOpen(true);
  };

  // closing the dropdown when clicking outside
  const onDocumentClick = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener(
      "click",
      onDocumentClick as unknown as EventListener
    );
    return () => {
      document.removeEventListener(
        "click",
        onDocumentClick as unknown as EventListener
      );
    };
  }, []);

  const getMenuItems = () =>
    dropdownMenuRef.current?.querySelectorAll("button");

  const getCurrentIndex = (itemsArray: NodeListOf<HTMLButtonElement>) =>
    Array.prototype.indexOf.call(itemsArray, document.activeElement);

  const focusNextEnabledItem = (
    itemsArray: NodeListOf<HTMLButtonElement>,
    startIndex: number,
    direction: "next" | "prev"
  ) => {
    // Calculate the new index based on direction
    const newIndex =
      direction === "next"
        ? (startIndex + 1) % itemsArray.length
        : (startIndex - 1 + itemsArray.length) % itemsArray.length;

    // If item isn't disabled, focus it directly
    if (!itemsArray[newIndex].disabled) {
      itemsArray[newIndex].focus();
      return;
    }

    // Otherwise find next enabled item
    let indexToFocus = newIndex;
    const increment = direction === "next" ? 1 : -1;

    while (indexToFocus >= 0 && indexToFocus < itemsArray.length) {
      if (!itemsArray[indexToFocus].disabled) {
        itemsArray[indexToFocus].focus();
        break;
      } else {
        indexToFocus += increment;
      }
    }
  };

  const handleItemSelection = () => {
    const activeElement = document.activeElement as HTMLElement;
    const clickableElement =
      activeElement.tagName === "BUTTON"
        ? activeElement
        : activeElement.querySelector("button");

    if (clickableElement) {
      clickableElement.click();
      setTimeout(() => {
        const parentElement = document.getElementById("payment-methods");
        if (parentElement) {
          const firstFocusableElement = parentElement.querySelector(
            "input[type='text'], select"
          );
          if (firstFocusableElement) {
            (firstFocusableElement as HTMLInputElement).focus();
          }
        }
      }, 1000);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    const itemsArray = getMenuItems();
    if (!itemsArray) return;

    const currentIndex = getCurrentIndex(itemsArray);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusNextEnabledItem(itemsArray, currentIndex, "next");
        break;

      case "ArrowUp":
        e.preventDefault();
        focusNextEnabledItem(itemsArray, currentIndex, "prev");
        break;

      case "Escape":
        setIsOpen(false);
        btnRef.current?.focus();
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        handleItemSelection();
        break;

      case "Tab":
        setIsOpen(false);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (isOpen) {
      const firstItem = dropdownMenuRef.current?.querySelector("a");
      firstItem?.focus();
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef}>
      <Button
        id={`flyout-menu`}
        tabIndex={-1}
        svg={
          buttonSvg ? (
            buttonSvg
          ) : (
            <DotsVertical
              role={"presentation"}
              focusable={false}
              aria-hidden="true"
            />
          )
        }
        text={buttonText}
        ariaLabel={ariaLabel}
        variant={buttonText ? "outline" : "icon"}
        onClick={(e) => onClickMenu(e as unknown as MouseEvent)}
        onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent)}
        style={buttonText ? {} : { padding: 0 }}
        ref={btnRef}
        testId={"flyout-menu"}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls="menu-id"
      />
      <DropdownBehaviour
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isVerticalPositionFlexible={true}
        ref={btnRef as RefObject<HTMLInputElement>}
        horizontalPosition={DropdownBehaviourHorizontalPositionEnum.Right}
      >
        <ul
          className={`${dropdownCoreClassUl} ${flyoutDropdownCoreClassUl}`}
          id="menu-id"
          role="menu"
          hidden={!isOpen}
          ref={dropdownMenuRef}
          onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent)}
          style={
            matchWidthWithButton &&
            btnRef.current?.getBoundingClientRect().width
              ? { width: btnRef.current?.getBoundingClientRect().width }
              : {}
          }
        >
          {optionsArray
            .filter((element) => !!element)
            .map((optionElement) => (
              <li
                key={`list-item-${optionElement?.key}`}
                className={`${dropdownCoreClassLi} ${flyoutDropdownCoreClassLi}`}
                data-testid="select-option"
                role="menuitem"
              >
                {optionElement}
              </li>
            ))}
        </ul>
      </DropdownBehaviour>
    </div>
  );
};
